<template>
  <div>
    <card-minimal-title :title="`${transactions.total} ${title}`" />
    <table-list class="table-sm">
      <template v-slot:table-header>
        <div class="list-header p-a-md-3 ai-md-c">
          <div class="only-mobile filter d-f" v-if="!hideFilters">
            <Button icon="sliders-24" :class="`ml-md-a small no-border-radius filter-button keep-hover blue f-1 no-padding only-icon ${mobileFilterOpen ? '' : 'only-text'}`" @click="mobileFilterOpen = true" :badge="filter.filters.length"></Button>
          </div>
          <div class="only-desktop f-1" v-if="!hideFilters">
            <Button icon="sliders-24" :class="`ml-md-a medium minimal-border-radius keep-hover blue with-extra-side-padding ${filterOpen ? '' : 'only-text'}`" @click="filterOpen = !filterOpen" :badge="filter.filters.length">Filters</Button>
          </div>
          <div class="search ml-md-a mr-md-a f-md-1-5" v-if="!disableSearch">
            <form-input-text class="f-w" icon="search-magnify-24" id="search" ref="search" placeholder="Search on email, transaction, ticket, broadcast or video" :value="search" @input="searchFunc">
              <template slot="search-results">
                <ul class="filter-search">
                  <template v-if="search === ''">
                    <li class="search-result-header">Latest created tickets</li>
                    <li v-for="ticket in searchTickets" :key="ticket.id" @click="addToFilter('product_id', { value: ticket.id, string: ticket.name })">
                      <i class="icon-tshirt-product-24" />
                      {{ ticket.name }}
                      <span>#{{ ticket.id }}</span>
                    </li>
                    <template v-if="suggestedBroadcasts.length > 0">
                      <li class="search-result-header">Previous broadcasts</li>
                      <li v-for="broadcast in suggestedBroadcasts" :key="broadcast.id" @click="addToFilter('item', { value: `live:${broadcast.id}`, string: broadcast.metadata.title })">
                        <i class="icon-stream-24" />
                        {{ broadcast.metadata.title }}
                        <span>#{{ broadcast.id }}</span>
                      </li>
                    </template>
                  </template>
                  <template v-else>
                    <li v-if="!/^\d+$/.test(search)" @click="addToFilter('play_user_email', { value: search, string: search })">
                      <i class="icon-user-24"/>
                      {{ search }}
                      <span>Email</span>
                    </li>
                    <li v-if="/^0/i.test(search)" @click="addToFilter('email', { value: search, string: search })">
                      <i class="icon-phone-smart-24"/>
                      {{ search }}
                      <span>Phone</span>
                    </li>
                    <li v-if="/^\d+$/.test(search) && !/^0/i.test(search) && search.length === 19" @click="addToFilter('transaction_id', { value: search, string: search })">
                      <i class="icon-accounting-24" />
                      {{ search }}
                      <span>Transaction ID</span>
                    </li>
                    <li class="search-result-header">Tickets</li>
                    <template v-if="searchTickets.length > 0">
                      <li v-for="ticket in searchTickets" :key="ticket.id" @click="addToFilter('product_id', { value: ticket.id, string: ticket.name })">
                        <i class="icon-tshirt-product-24" />
                        {{ ticket.name }}
                        <span>#{{ ticket.id }}</span>
                      </li>
                    </template>
                    <template v-else>
                      <p>No hits on tickets</p>
                    </template>
                    <li class="search-result-header">Broadcasts</li>
                    <template v-if="searchBroadcasts.length > 0">
                      <li v-for="broadcast in searchBroadcasts" :key="broadcast.id" @click="addToFilter('item', { value: `live:${broadcast.id}`, string: broadcast.metadata.title })">
                        <i class="icon-stream-24" />
                        {{ broadcast.metadata.title }}
                        <span>#{{ broadcast.id }}</span>
                      </li>
                    </template>
                    <template v-else>
                      <p>No hits on broadcasts</p>
                    </template>
                  </template>
                </ul>
              </template>
            </form-input-text>
          </div>
          <div class="only-mobile buttons f-w d-f">
              <div class="button">
                <Button :class="`f-w medium only-text keep-hover no-border-radius padding ${checked.all ? 'red' : 'blue'}`">{{ checked.all ? 'Unselect all' : 'Select all' }}</Button>
              </div>
              <div class="button">
                <Button class="f-w medium only-text keep-hover blue no-border-radius padding" @click="mobileSortOpen = true">Sort…</Button>
              </div>
              <div class="button">
                <Button icon="accounting-24" class="f-w small only-text only-icon no-border-radius keep-hover blue padding" @click="exportTransactions"></Button>
              </div>
            </div>
          <div class="only-desktop d-f f-1">
            <div class="button ml-a">
              <Button icon="accounting-24" class="medium only-text keep-hover blue minimal-border-radius with-extra-side-padding" @click="exportTransactions">Export</Button>
            </div>
          </div>
        </div>
        <template v-if="filterOpen">
          <list-filter
            class="only-desktop"
            :filter="filter"
            :disableTypes="!!forceType"
            @removeFilter="removeFromFilter"
          />
        </template>
        <portal to="modals">
          <modal :show="mobileFilterOpen" @close="mobileFilterOpen = false" class="auto-height" title="Filter transactions">
            <list-filter
              :filter="filter"
              :disableTypes="!!forceType"
              @removeFilter="removeFromFilter"
            />
          </modal>
        </portal>
      </template>
      <tr class="header only-desktop">
        <th class="w-5p ta-r">Amount</th>
        <th class="w-10p" v-if="!hideTypes"></th>
        <th class="w-25p" v-if="!skipTicket">Ticket</th>
        <th class="w-100p w-md-100p">Customer</th>
        <th class="w-10p" :data-sorting-on="sort.sort === 'created'" :data-sorting-direction="sort.direction">Date</th>
      </tr>
      <a :href="`/transaction/${transaction.id}`" :class="`${selectedTransaction && transaction.id === selectedTransaction.transaction ? 'selected' : ''}`" @click.prevent="handleLink($event, `/transaction/${transaction.id}`)" v-for="(transaction, index) in transactions.list" :key="index">
        <td class="ta-r only-desktop">{{ transaction.formated_amount }}</td>
        <td class="only-desktop" v-if="!hideTypes">
          <template v-if="(transaction.status == 'completed' || transaction.status == 'pending_transfer')">
            <Tag :text="$t(`commerce.status_${transaction.status}`)" :icon="'check'" :class="{'capitalize': true}" />
          </template>
          <template v-else>
            <Tag :text="$t(`commerce.status_${transaction.status}`)" :class="{'capitalize': true, 'gray': ((transaction.status != 'failed') ? true:false ), 'red': (transaction.status == 'failed')}" />
          </template>
          <Tag :text="$t(`commerce.${transaction.type}`)" :class="{'purple': true, 'capitalize': true, 'm-l-4': true}" />
        </td>
        <td class="ellipsis only-desktop" v-if="!skipTicket">
          <div>{{ ticketName(transaction.product_id) }}</div>
        </td>
        <td class="ellipsis w-100p w-md-50p">
          <div class="blue">{{ transaction.customer_reference }}</div>
          <div class="only-mobile">
            <div class="d-f f-wrap gray customer-dates p-t-1">
              <div class="d-f ai-c m-r-2">
                <i class="icon-clock-fill-24" /> <span class="tt-c">{{ transaction.created_at | formatdate }}</span>
                <span v-if="!skipTicket">&nbsp;- {{ ticketName(transaction.product_id) }}</span>
              </div>
            </div>
            <div class="m-t-1">
              <template v-if="transaction.amount !== 0">
                <span class="m-r-1">{{ transaction.amount / 100 }} {{ transaction.currency }}</span>
              </template>
              <template v-if="!hideTypes">
                <template v-if="(transaction.status == 'completed' || transaction.status == 'pending_transfer')">
                  <Tag :text="$t(`commerce.status_${transaction.status}`)" :icon="'check'" :class="{'capitalize': true}" />
                </template>
                <template v-else>
                  <Tag :text="$t(`commerce.status_${transaction.status}`)" :class="{'capitalize': true, 'gray': ((transaction.status != 'failed') ? true:false ), 'red': (transaction.status == 'failed')}" />
                </template>
                <Tag :text="$t(`commerce.${transaction.type}`)" :class="{'purple': true, 'capitalize': true, 'm-l-4': true}" />
              </template>
            </div>
          </div>
        </td>
        <td class="only-desktop">{{ transaction.created_at | formatdate }}</td>
      </a>
    </table-list>
    <pagination class="m-t-4 m-b-2" :page="transactions.page" :maxPage="transactions.max_page" @page="paginate" />
  </div>
</template>

<script>

import moment from 'moment';
import { exportToCsv } from '@/utils';

import Tag from '@/components/tag.vue';
import TableList from '@/components/Lists/Table.vue';
import Pagination from '@/components/Lists/Pagination.vue';
import ListFilter from '@/components/Commerce/ListFilter.vue';

export default {
  components: {
    Pagination,
    TableList,
    ListFilter,
    Tag,
  },
  props: {
    disableSearch: {
      type: Boolean,
      default: false,
    },
    selectedTransaction: {
      type: Object,
      default: null,
    },
    forceEmail: {
      type: String,
      default: null,
    },
    forceType: {
      type: String,
      default: null,
    },
    forceStatus: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: 'transactions',
    },
    skipTicket: {
      type: Boolean,
      default: false,
    },
    hideFilters: {
      type: Boolean,
      default: false,
    },
    hideTypes: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    searchTickets() {
      return this.tickets.filter(t => this.search.toLowerCase().split(' ').every(word => t.name.toLowerCase().indexOf(word) > -1)).slice(0, 3);
    },
    /**
     * The list of tickets that exists
     * @returns {Array}
     */
    tickets() {
      return this.$store.getters['Products/allStreams'];
    },
  },
  methods: {
    searchFunc(value) {
      this.search = value;

      clearTimeout(this.searchTimeout);

      this.searchTimeout = setTimeout(async () => {
        if (value === '') {
          this.searchBroadcasts = [];
        }

        // Search broadcasts by title
        const payload = {
          page: 1,
          per_page: 3,
          type: 'past',
          title: `${value}`,
          skipConf: true,
          skipLoading: true,
          skipState: true,
        };
        const { broadcasts } = await this.$store.dispatch('Broadcasts/getBroadcasts', payload);
        this.searchBroadcasts = broadcasts;
      }, 250);
    },
    addToFilter(type, value) {
      const filterIndex = this.filter.filters.findIndex(f => f.type === type);

      if (filterIndex >= 0) {
        this.filter.filters.splice(filterIndex, 1);
      }

      this.filter.filters.push({ type, value });

      this.search = '';
      this.$refs.search.clear();

      this.$router.push({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          [type]: value.value === value.string ? value.value : `${value.value};${value.string}`,
        },
      });

      this.filterOpen = true;
    },
    removeFromFilter(filter) {
      const filterIndex = this.filter.filters.findIndex(f => f.type === filter.type);
      if (filterIndex >= 0) {
        this.filter.filters.splice(filterIndex, 1);

        const query = Object.assign({}, this.$route.query);
        delete query[filter.type];
        this.$router.replace({ query });
      }
    },
    searchTransaction(value) {
      this.filter.search = value;
      this.filterOpen = true;
    },
    /**
     * Check all transactions
     * @returns {Void}
     */
    checkAll() {
      this.checked.all = !this.checked.all;
      this.checked.ids = this.checked.all ? this.transactions.list.map(customer => customer.id) : [];
    },
    /**
     * Add transaction id to checked ids array for export
     * @param {String} id
     */
    checkTransaction(id) {
      const index = this.checked.ids.indexOf(id);

      if (index >= 0) {
        this.checked.ids.splice(index, 1);

        if (this.checked.all) {
          this.checked.all = false;
        }

        return;
      }

      this.checked.ids.push(id);
    },
    async exportTransactions() {
      const {
        filter: {
          types,
          created,
          status,
          filters,
        },
      } = this;

      const payload = {};
      const warnFilter = [];

      filters.forEach((filter) => {
        if (['item', 'product_id', 'transaction_id'].includes(filter.type)) {
          payload[filter.type] = filter.value.value;
        } else {
          // eslint-disable-next-line
          if (filter.type === 'play_user_email') {
            warnFilter.push('E-mail');
          } else if (filter.type === 'email') {
            warnFilter.push('Phone number');
          }
        }
      });

      if (warnFilter.length > 0) {
        const filterWarnText = `${warnFilter.length > 1 ? `Filters ${warnFilter.join(' and ')}` : `Filter ${warnFilter[0]}`} are not supported when exporting transactions. ${warnFilter.length > 1 ? 'These filters' : 'This filter'} will be ignored during export.`;
        const willExport = await swal({
          title: 'Exporting transactions', text: filterWarnText, icon: 'warning', buttons: true, dangerMode: true,
        });

        if (!willExport) {
          return;
        }
      }

      if (types.filter(t => t.selected).length > 0) {
        payload.type = types.filter(t => t.selected).map(t => t.value).join(',');
      }

      if (status.filter(s => s.selected).length > 0) {
        payload.status = status.filter(s => s.selected).map(s => s.value).join(',');
      }

      if (created.type === 'specific') {
        payload.start = created.from;
        payload.end = created.to;
      }

      if (this.forceEmail) {
        payload.email = this.forceEmail;
      }

      if (this.forceType) {
        payload.type = this.forceType;
      }

      if (this.forceStatus) {
        payload.status = this.forceStatus;
      }

      const data = await this.$store.dispatch('Transactions/exportTransactions', payload);

      if (!data) {
        swal({ title: 'An error occured', text: 'Could not export transactions', icon: 'error' });
        return;
      }

      exportToCsv('transactions_export.csv', data);
    },
    /**
     * Get transactions
     * @returns {Void}
     */
    getTransactions() {
      clearTimeout(this.getTimeout);

      this.getTimeout = setTimeout(async () => {
        const {
          transactions: {
            page,
            per_page,
          },
          sort: {
            sort,
            direction,
          },
          filter: {
            types,
            created,
            status,
            filters,
          },
        } = this;

        const payload = {
          page,
          per_page,
          sort,
          direction,
          skipConf: true,
          skipState: true,
        };

        filters.forEach((filter) => {
          payload[filter.type] = filter.value.value;
        });

        if (types.filter(t => t.selected).length > 0) {
          payload.type = types.filter(t => t.selected).map(t => t.value).join(',');
        }

        if (status.filter(s => s.selected).length > 0) {
          payload.status = status.filter(s => s.selected).map(s => s.value).join(',');
        }

        if (created.type === 'specific') {
          payload.start = created.from;
          payload.end = created.to;
        }

        if (this.forceEmail) {
          payload.email = this.forceEmail;
        }

        if (this.forceType) {
          payload.type = this.forceType;
        }

        if (this.forceStatus) {
          payload.status = this.forceStatus;
        }

        const { transactions, pagination, total } = await this.$store.dispatch('Transactions/getTransactions', payload);

        this.transactions = {
          ...this.transactions,
          list: transactions,
          max_page: pagination.max_page,
          page: pagination.current_page,
          total,
        };
      }, 200);
    },
    /**
     * Paginate in video list
     * @param {Number} page
     * @returns {Void}
     */
    paginate(page) {
      this.transactions.page = page;

      this.$router.push({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          page,
        },
      });

      this.getTransactions();
    },
    /**
     * Return the name of the ticket that matches the ID
     * @returns {String}
     */
    ticketName(id) {
      return this.tickets.find(t => t.id === id)?.name;
    },
  },
  watch: {
    filter: {
      deep: true,
      handler(filter) {
        const query = Object.assign({}, this.$route.query);

        const type = filter.types.filter(t => t.selected).map(t => t.type).join(',');
        if (type === '') {
          delete query.type;
        } else {
          query.type = type;
        }

        const status = filter.status.filter(t => t.selected).map(t => t.type).join(',');
        if (status === '') {
          delete query.status;
        } else {
          query.status = status;
        }

        const validFilters = ['item', 'product_id', 'transaction_id', 'play_user_email', 'email'];
        const unusedFilters = validFilters.filter(vf => filter.filters.map(f => (f.type)).indexOf(vf) === -1);
        unusedFilters.forEach(uf => delete query[uf]);

        if (filter.created.type === 'all') {
          delete query.created;
        } else {
          query.created = `${filter.created.from};${filter.created.to}`;
        }

        // eslint-disable-next-line
        this.$router.replace({ query }).catch(err => {});

        this.getTransactions();
      },
    },
  },
  data() {
    return {
      getTimeout: null,
      transactions: {
        list: [],
        total: 0,
        page: 1,
        per_page: 20,
        max_page: 20,
      },
      checked: {
        all: false,
        ids: [],
      },
      sort: {
        sort: 'created',
        direction: 'desc',
      },
      search: '',
      filter: {
        product_id: null,
        filters: [],
        types: [
          // eslint-disable-next-line
          { type: 'card', value: 'stripe', string: 'Card', selected: false },
          // eslint-disable-next-line
          { type: 'swish', value: 'swish', string: 'Swish', selected: false },
          // eslint-disable-next-line
          { type: 'donation', value: 'stripe_donation', string: 'Donation', selected: false },
          // eslint-disable-next-line
          { type: 'voucher', value: 'voucher', string: 'Voucher', selected: false },
        ],
        status: [
          // eslint-disable-next-line
          { type: 'completed', value: 'completed,pending_transfer', string: 'Succeeded', selected: false },
          // eslint-disable-next-line
          { type: 'pending', value: 'pending', string: 'Incomplete', selected: false },
          // eslint-disable-next-line
          { type: 'failed', value: 'failed', string: 'Failed', selected: false },
          // eslint-disable-next-line
          { type: 'refunded', value: 'refunded', string: 'Refunded', selected: false },
        ],
        created: {
          type: 'all',
          from: moment().subtract(6, 'months').format('YYYY-MM-DD'),
          to: moment().format('YYYY-MM-DD'),
        },
      },
      suggestedBroadcasts: [],
      searchBroadcasts: [],
      searchTimeout: null,
      filterOpen: false,
      mobileFilterOpen: false,
    };
  },
  async mounted() {
    const {
      page = 1,
      sort = 'created',
      direction = 'desc',
    } = this.$queryHelper.get({ validSort: ['created'] });

    const {
      type,
      created,
      search,
      status,
      product_id,
      transaction_id,
      play_user_email,
      item,
      email,
    } = this.$route.query;

    if (type && type !== '') {
      const typeSplit = type.split(',');
      this.filter.types.forEach((t) => {
        if (typeSplit.includes(t.type)) {
          // eslint-disable-next-line
          t.selected = true;
        }
      });
    }

    if (status && status !== '') {
      const statusSplit = status.split(',');
      this.filter.status.forEach((s) => {
        if (statusSplit.includes(s.type)) {
          // eslint-disable-next-line
          s.selected = true;
        }
      });
    }

    if (search && search !== '') {
      this.filter.search = search;
    }

    if (created && created !== '') {
      const createdSplit = created.split(';');
      this.filter.created = {
        type: 'specific',
        from: createdSplit[0],
        to: createdSplit[1],
      };
    }

    if (email && email !== '') {
      this.filter.filters.push({ type: 'email', value: { value: email, string: email } });
    }

    if (play_user_email && play_user_email !== '') {
      this.filter.filters.push({ type: 'play_user_email', value: { value: play_user_email, string: play_user_email } });
    }

    if (transaction_id && transaction_id !== '') {
      this.filter.filters.push({ type: 'transaction_id', value: { value: transaction_id, string: transaction_id } });
    }

    if (product_id && product_id !== '') {
      const product_idSplit = product_id.split(';');
      this.filter.filters.push({
        type: 'product_id',
        value: {
          value: product_idSplit[0],
          string: product_idSplit[1],
        },
      });
    }

    if (item && item !== '') {
      const itemSplit = item.split(';');
      this.filter.filters.push({
        type: 'item',
        value: {
          value: itemSplit[0],
          string: itemSplit[1],
        },
      });
    }

    this.transactions = {
      ...this.transactions,
      page,
    };

    this.sort = {
      ...this.sort,
      sort,
      direction,
    };

    if (!this.skipTicket) {
      await this.$store.dispatch('Products/getAllStreams');
    }
    await this.getTransactions();

    const { broadcasts } = await this.$store.dispatch('Broadcasts/getBroadcasts', {
      page: 1,
      per_page: 3,
      type: 'past',
      direction: 'desc',
      skipConf: true,
      skipLoading: true,
      skipState: true,
    });

    this.suggestedBroadcasts = broadcasts;
  },
  beforeDestroy() {
    clearTimeout(this.getTimeout);
  },
};
</script>

<style lang="scss" scoped>
.list-header {
  display: flex;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    .filter {
      border-bottom: 1px solid #d8dae1;
      width: 60px;

      .filter-button {
        border-radius: 10px 0 0 0;
      }
    }

    .search {
      width: calc(100% - 60px);

      .input {
        border-radius: 0;
        border-top: 0;
        border-right: 0;
      }
    }

    .buttons {
      & > * {
        border-bottom: 1px solid #d8dae1;
        flex: 1;

        &:not(:last-child) {
          border-right: 1px solid #d8dae1;
        }
      }
    }
  }
}

.filter-search {
  li {
    align-items: center;
    position: relative;
    display: flex;

    i {
      color: #868B97;
      position: absolute;
      font-size: 24px;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
    }

    span {
      color: rgb(108, 108, 108);
      font-family: Menlo, Consolas, monospace;
      margin-left: auto;
      font-size: 10px;
    }

    &:hover:not(.search-result-header) {
      background-color: #0060FF;
      color: white;
      cursor: pointer;

      i {
        color: white;
      }

      span {
        color: rgb(209, 209, 209);
      }
    }

    &:last-child {
      border-radius: 0 0 20px 20px;
    }
  }
}
</style>
